import {
	queryClient,
	SociosSocketEvent,
	type SocketsListener,
	useSocketEventListener,
	withRefreshStrategy
} from '@capsicum/common';
import { useMemo } from 'react';

import { UseRPAuctionsMeQueryKeyBuilder } from '../../data-access/queries/useRPAuctionsQueries';

// WS events:
// auction:status:updated - triggered when the auction changes status, usually from enabled to disabled and finished.
// auction:bid:accepted - triggered when there is a new bid accepted in a auction
// auction:bid:outbid - triggered when the user got outbid in a auction
// fanShop:product:updated - triggered when there is a change on a product variant, e.g. title, or description

export const useUserAuctionsUpdateFromWebSocket = ({
	callback,
	isDismissed = false,
	userAuctionIds,
	userAuctionProductIds
}: {
	callback?: () => void;
	isDismissed?: boolean;
	userAuctionIds?: Array<string>;
	userAuctionProductIds?: Array<string>;
}) => {
	const listeners = useMemo(
		() =>
			new Map<SociosSocketEvent, SocketsListener>([
				[
					SociosSocketEvent.FANSHOP_PRODUCT_UPDATED,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (userAuctionProductIds?.includes(data?.payload?.id)) {
								queryClient.invalidateQueries({
									queryKey: [...UseRPAuctionsMeQueryKeyBuilder[0]]
								});
							}
						}
					})
				],
				[
					SociosSocketEvent.AUCTION_STATUS_UPDATED,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (userAuctionIds?.includes(data?.payload?.id)) {
								queryClient.invalidateQueries({
									queryKey: UseRPAuctionsMeQueryKeyBuilder
								});
							}
						}
					})
				],
				[
					SociosSocketEvent.AUCTION_BID_OUTBID,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (userAuctionIds?.includes(data?.payload?.auction?.id)) {
								queryClient.invalidateQueries({
									queryKey: UseRPAuctionsMeQueryKeyBuilder
								});
								callback?.();
							}
						}
					})
				],
				[
					SociosSocketEvent.AUCTION_BID_ACCEPTED,
					withRefreshStrategy({
						listener: data => {
							if (isDismissed) return;
							if (userAuctionIds?.includes(data?.payload?.auction?.id)) {
								queryClient.invalidateQueries({
									queryKey: UseRPAuctionsMeQueryKeyBuilder
								});
								callback?.();
							}
						}
					})
				]
			]),
		[userAuctionIds, userAuctionProductIds, callback, isDismissed]
	);

	useSocketEventListener(listeners, !!userAuctionIds?.length);
};
